import { HttpStatusCode } from '@angular/common/http';
import { APP_BOOTSTRAP_LISTENER, Inject, InjectionToken, SecurityContext, Type } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { InvitationsStatus } from '@app/+state/invitation/domain';
import { TENANT_MGMT_ROUTES, TenantActionItem, TenantRow } from '@app/+state/user-data/user.model';
import { ENV_VARS } from '@app/models/config.model';
import { ApiError } from '@core/api-error';
import { AppErrorCode } from '@core/common-constants';
import { AllocationDetails, EntitylementTypes, RASymbols } from '@lemans/shared';
import { EffectSources } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  AgFilterContext,
  AgFilterModel,
  AgGridEntitlementColumnsFormat,
  AppFlow,
  BadgeInfo,
  DefaultLanguage,
  DisabledService,
  DisplayResourceType,
  Entitlement,
  EntitlementBadge,
  Env,
  FilterContext,
  FilterModel,
  FilterType,
  FlagValue,
  ITenantDetail,
  Resource,
  ResourceType,
  ResourceTypeString,
  Role,
  Service,
  SnackBarType,
  TOKEN_BALANCE_FRACTION_DIGITS,
  UiStateActions,
  navigateToError,
  noLastAccessedTenant,
} from '@ra-state';
import { ActionButtonStyles, DialogService, DialogSizes, IDialogConfig } from '@ra-web-tech-ui-toolkit/components';
import { AppId } from '@rockwell-automation-inc/common-utils';
import { ICellRendererParams } from 'ag-grid-community';
import { DateTime, Duration } from 'luxon';
import * as R from 'remeda';
import { Observable, from } from 'rxjs';

export const BOOTSTRAP_EFFECTS = new InjectionToken('Bootstrap Effects');

export function bootstrapEffects(effects: Type<any>[], sources$: EffectSources) {
  return (): void => {
    effects.forEach((effect) => sources$.addEffects(effect));
  };
}

export function createInstances(...instances: any[]): any[] {
  return instances;
}

export function provideBootstrapEffects(effects: Type<any>[]): any[] {
  return [
    effects,
    { deps: effects, provide: BOOTSTRAP_EFFECTS, useFactory: createInstances },
    {
      deps: [[new Inject(BOOTSTRAP_EFFECTS)], EffectSources],
      multi: true,
      provide: APP_BOOTSTRAP_LISTENER,
      useFactory: bootstrapEffects,
    },
  ];
}

export function getEnv(key: ENV_VARS): any {
  const runtimeEnv = globalThis as typeof globalThis & Record<string, unknown>;

  if (!(key in runtimeEnv) || runtimeEnv[key] === undefined) {
    throw new Error(`Env var not defined ${key}`);
  }
  const value = runtimeEnv[key];
  if (typeof value === 'string' && value === '') {
    throw new Error(`Env var is defined ${key} with value ''`);
  }

  return value;
}

export function getEnvOrDefault(key: ENV_VARS, defaultVal): any {
  try {
    return getEnv(key);
    // eslint-disable-next-line sonarjs/no-ignored-exceptions
  } catch (_error) {
    return defaultVal;
  }
}

export function getBadgeInfo(value: string): BadgeInfo {
  switch (value) {
    case EntitylementTypes.addOns:
      return { color: 'lightBlue', text: 'Add-Ons' };
    case EntitylementTypes.trial:
      return { color: 'darkBlue', text: EntitylementTypes.trial };
    case EntitylementTypes.platform:
      return { color: 'blue', text: EntitylementTypes.platform };
    case EntitylementTypes.additive:
      return { color: 'purple', text: EntitylementTypes.additive };
    case EntitylementTypes.utilityToken:
      return { color: 'purple', text: 'Universal Credits' };
    default:
      return { color: 'none', text: RASymbols.MINUS };
  }
}

export function formatEntitlementCopyColumnContent(data: AgGridEntitlementColumnsFormat): any {
  return `${data.header} ${data.subheader}`;
}

export function mapServiceData(params: Entitlement): any {
  // REVISIT: this func is extremely fishy - see this.allApps below - it's coming from the default this from
  const serviceKind = params?.serviceKind;
  const appName = serviceKind ? getAppNameOrDefault(this.allApps, serviceKind) : '';
  return {
    header: this.catalogInfos.get(params?.catalogNumber)?.name || params?.catalogNumber,
    subheader: appName + 'Catalog: ' + params?.catalogNumber,
  };
}

function getAppNameOrDefault(allApps, serviceKind): any {
  return (allApps.get(serviceKind)?.appName || serviceKind) + ' | ';
}

export function dateIsValid(date): any {
  return !Number.isNaN(new Date(date).getTime());
}

export function formatBadgeCopyColumnContent(badge: string): any {
  return EntitlementBadge[badge as unknown as keyof typeof EntitlementBadge];
}

export function openDialogWrapper$(dialogSvc: DialogService, config: IDialogConfig): Observable<any> {
  const dialog = dialogSvc.openDialog(config);
  return dialog.componentInstance.onClick;
}

export function getDisplayResourceType(v: ResourceTypeString, isSubOrg?: boolean): DisplayResourceType {
  const resourceTypes: Record<ResourceTypeString, DisplayResourceType> = {
    Tenant: isSubOrg ? 'Sub-organization' : 'Organization',
    Service: 'Service',
  };
  return resourceTypes[v] || v;
}

export function toFilterModel(agFilterModel: AgFilterModel): FilterModel {
  const filterModel: FilterModel = {};
  Object.keys(agFilterModel).forEach((key) => {
    const agFilterContext: AgFilterContext = agFilterModel[key];
    const filterContext: FilterContext = {
      type: agFilterModel[key].type,
      filterType: agFilterModel[key].filterType,
    };

    switch (agFilterContext.filterType) {
      case 'number':
      case 'text':
        filterContext.filter = agFilterModel[key].filter;
        filterContext.filterTo = agFilterModel[key].filterTo;
        break;

      case 'set':
        filterContext.values = agFilterModel[key].values?.map((value) => {
          if (value === null) {
            return '';
          }
          return value;
        });
        break;

      case 'date':
        filterContext.dateFrom = getDateWithMaxOrMinDayTime(`${agFilterContext?.dateFrom}`, agFilterContext?.type);
        filterContext.dateTo = agFilterContext.dateTo
          ? getDateWithMaxDayTime(`${agFilterContext?.dateTo}`)
          : new Date();
        break;

      default:
        break;
    }
    filterModel[key] = filterContext;
  });
  return filterModel;
}

export function getDateWithMaxOrMinDayTime(value: string, filterType?: FilterType): Date {
  switch (filterType) {
    case 'lessThanOrEqual':
      return getDateWithMaxDayTime(value);
    case 'greaterThanOrEqual':
      return getDateWithMinDayTime(value);
    case 'inRange':
      return getDateWithMinDayTime(value);
    default: {
      const datePart = value.split(' ')[0];
      return new Date(datePart);
    }
  }
}

export function getDateWithMaxDayTime(value: string): Date {
  const datePart = value.split(' ')[0];
  return new Date(`${datePart}T23:59:59`);
}

export function getDateWithMinDayTime(value: string): Date {
  const datePart = value.split(' ')[0];
  return new Date(`${datePart}T00:00:01`);
}

export class Awaiter {
  promises: Map<Promise<boolean>, any> = new Map();
  createPromise(): Promise<boolean> {
    let deferred: any;
    const p = new Promise<boolean>((resolve, reject) => (deferred = { resolve, reject }));
    this.promises.set(p, deferred);
    return p;
  }
  resolve(p: Promise<boolean>): void {
    this.promises.get(p).resolve();
  }
  reject(p: Promise<boolean>): void {
    this.promises.get(p).reject();
  }
  whenAll(): Promise<any> {
    const promises = [...this.promises.keys()];
    if (promises.length === 0) {
      return new Promise<void>((resolve, _reject) => resolve());
    }
    return Promise.all(promises);
  }

  whenAnySettles(): Promise<any> {
    const promises = [...this.promises.keys()];
    if (promises.length === 0) {
      return new Promise<void>((resolve, _reject) => resolve());
    }
    return Promise.race(promises);
  }
  whenAllSettled(): Promise<any> {
    return Promise.allSettled(this.promises.keys());
  }
  whenFirstSuccess(): Promise<any> {
    return Promise.any(this.promises.keys());
  }
  any = this.whenFirstSuccess;
  race = this.whenAnySettles;
  allSettled = this.whenAllSettled;
  get all$(): Observable<any> {
    return from(this.whenAll());
  }
  get any$(): Observable<any> {
    return from(this.any());
  }
}

export function openURL(url: string): void {
  window.open(url, '_blank');
}

export function roundToThreeDecimals(value: number): number {
  const roundingFactor = Math.pow(10, TOKEN_BALANCE_FRACTION_DIGITS);
  return Math.ceil(value * roundingFactor) / roundingFactor;
}

export function modifyFilterValuesforCredit(filterModel: FilterModel, colName: string): FilterModel {
  const filterModelClone = R.clone(filterModel);
  if (filterModelClone[colName]) {
    filterModelClone['hasCredits'] = {
      ...filterModelClone[colName],
      values: filterModelClone[colName].values?.map((value) => (value === 'with-credits' ? 'True' : 'False')),
    };
    delete filterModelClone[colName];
  }
  return filterModelClone;
}

export function getInvitationStatus(status: InvitationsStatus): string {
  let statusText: string = status;
  if (status === InvitationsStatus.ACCEPTEDALERT) {
    statusText = 'Accepted by different email';
  } else if (status === InvitationsStatus.ACTIVE) {
    statusText = 'Sent';
  }
  return statusText;
}

export function checkIfTrialEntitlement(entitlement: Entitlement): boolean {
  return (
    (entitlement.attributes && Object.hasOwn(entitlement.attributes, 'campaignId')) ||
    entitlement.catalogNumber === 'TRIAL-CAMPAIGN-CREDITS'
  );
}

/**
 * The backend doesn't always return an `id` for an entitlement. When missing, falls back to a
 * composite of contractNumber/catalogNumber.
 */
export function computeEntitlementId(entitlement: Entitlement): string {
  return entitlement.id || `${entitlement.contractNumber}-${entitlement.catalogNumber}`;
}

/**
 * Merges multiple entitlement rows that share the same `id` (see computeEntitlementId - computed
 * internally, so callers don't need to pre-populate `id`) into a single row. The backend can
 * return several separate allocation batches for what is effectively the same entitlement (e.g. a
 * quantity top-up done at different times), each with its own `allocationDetails`. Rather than
 * showing one grid row per batch, this combines all batches sharing an id into a single row:
 * `allocationDetails` is the concatenation of every batch's allocations, and `allocated`/`consumed`
 * are summed across them. The earliest batch (by `timestamp`) is used as `earliestEntitlement`, the
 * base for the remaining fields (catalogNumber, contractNumber, effectiveDate, expiryDate,
 * attributes, etc.), which are expected to be identical across batches sharing the same id.
 */
export function mergeEntitlementAllocationDetails<T extends Entitlement>(entitlements: T[]): T[] {
  const entitlementsById = R.groupBy(entitlements, (entitlement) => computeEntitlementId(entitlement));

  return Object.values(entitlementsById).map((entitlementBatches) => {
    if (entitlementBatches.length === 1) {
      return entitlementBatches[0];
    }

    const [earliestEntitlement] = R.sortBy(entitlementBatches, (entitlement) =>
      new Date(entitlement.timestamp).valueOf(),
    );
    const hasConsumed = entitlementBatches.some((entitlement) => entitlement.consumed !== undefined);

    return {
      ...earliestEntitlement,
      allocated: R.sumBy(entitlementBatches, (entitlement) => entitlement.allocated ?? 0),
      consumed: hasConsumed
        ? R.sumBy(entitlementBatches, (entitlement) => entitlement.consumed ?? 0)
        : earliestEntitlement.consumed,
      allocationDetails: entitlementBatches.flatMap((entitlement) => entitlement.allocationDetails ?? []),
    };
  });
}

/**
 * Immutably patches the `userInformation` of a single allocation (matched by `allocationId`) on
 * whichever entitlement matches the given `entitlementId` (see computeEntitlementId).
 */
export function updateAllocationUserInformation<T extends Entitlement>(
  entitlements: T[] | undefined,
  entitlementId: string,
  allocationId: string,
  userDescription: string,
): T[] | undefined {
  if (!entitlements) {
    return entitlements;
  }
  return entitlements.map((entitlement) => {
    const rowEntitlementId = computeEntitlementId(entitlement);
    if (rowEntitlementId !== entitlementId || !entitlement.allocationDetails) {
      return entitlement;
    }
    return {
      ...entitlement,
      allocationDetails: entitlement.allocationDetails.map((allocation): AllocationDetails =>
        allocation.allocationId === allocationId ? { ...allocation, userInformation: userDescription } : allocation,
      ),
    };
  });
}

/**
 * Applies updateAllocationUserInformation across every service's `entitlements` array,
 * preserving array/object identity for services whose entitlements aren't affected.
 */
export function updateServicesAllocationUserInformation(
  services: Service[] | undefined,
  entitlementId: string,
  allocationId: string,
  userDescription: string,
): Service[] | undefined {
  if (!services) {
    return services;
  }
  return services.map((service) => ({
    ...service,
    entitlements:
      updateAllocationUserInformation(service.entitlements, entitlementId, allocationId, userDescription) ??
      service.entitlements,
  }));
}

/**
 * Merges each service's `entitlements` (see mergeEntitlementAllocationDetails). Services without
 * entitlements are returned unchanged.
 */
function mergeServiceEntitlements<T extends Service>(services: T[] | undefined): T[] | undefined {
  if (!services) {
    return services;
  }
  return services.map((service) => ({
    ...service,
    entitlements: mergeEntitlementAllocationDetails(service.entitlements),
  }));
}

/**
 * Merges entitlements sharing the same `id` (see mergeEntitlementAllocationDetails) for the
 * tenant's `services`, `scheduledForActivationServices`, and `disabledServices`. `pendingServices`
 * (CSP entitlements) never render the nested allocation-id grid, so merging their entitlements
 * would have no effect on the UI; they're returned unchanged.
 */
export function mergeTenantDetailEntitlements(tenantDetail: ITenantDetail): ITenantDetail {
  return {
    ...tenantDetail,
    services: mergeServiceEntitlements<Service>(tenantDetail.services),
    scheduledForActivationServices: mergeServiceEntitlements<Service>(tenantDetail.scheduledForActivationServices),
    disabledServices: mergeServiceEntitlements<DisabledService>(tenantDetail.disabledServices),
  };
}

export function getServiceEntitlements(tenantDetails: ITenantDetail): Entitlement[] {
  /**
   * @param entitlement The entitlement object whose allocation timestamps need to be normalized.
   * @returns A new entitlement object with allocation timestamps converted to Date objects.
   */

  const normalizeAllocationTimestamps = (entitlement: Entitlement): Entitlement => ({
    ...entitlement,
    allocationDetails: entitlement.allocationDetails?.map((allocationDetails) => {
      const allocationTimestamp = allocationDetails.allocationTimestamp as Date | string;
      return {
        ...allocationDetails,
        allocationTimestamp: allocationTimestamp instanceof Date ? allocationTimestamp : new Date(allocationTimestamp),
      };
    }),
  });
  const normalizeActivationDate = (activationDate: Date | undefined): Date | undefined => {
    if (activationDate == null) {
      return undefined;
    }
    return new Date(activationDate);
  };
  const mapEntitlements = (services: Service[]): Entitlement[] => {
    return (
      services
        ?.flatMap((service) => {
          return service.entitlements.map((entitlement) => ({
            ...normalizeAllocationTimestamps(entitlement),
            serviceKind: service.kind,
            isTrialEntitlement: checkIfTrialEntitlement(entitlement),
          }));
        })
        ?.filter((entitlement) => !entitlement.isSystemGenerated) || []
    );
  };
  const pendingServicesEntitlements = tenantDetails.pendingServices
    ? tenantDetails.pendingServices
        .flatMap((pendingService) => {
          if (!pendingService.entitlement) {
            return [];
          }
          return [
            {
              ...normalizeAllocationTimestamps(pendingService.entitlement),
              activationDate: normalizeActivationDate(pendingService.entitlement.activationDate),
              serviceKind: pendingService.kind,
              isTrialEntitlement: checkIfTrialEntitlement(pendingService.entitlement),
              isCustomProvisioning: true,
            },
          ];
        })
        .filter((entitlement) => !entitlement.isSystemGenerated)
    : [];
  const scheduledServicesEntitlements = mapEntitlements(tenantDetails.scheduledForActivationServices || []);

  const provisionedServicesEntitlements = mapEntitlements(tenantDetails.services || []);
  const disabledServiceEntitlements = mapEntitlements(tenantDetails.disabledServices || []);

  return [
    ...provisionedServicesEntitlements,
    ...pendingServicesEntitlements,
    ...scheduledServicesEntitlements,
    ...disabledServiceEntitlements,
  ];
}

export function getTimeLeft(endDate: string): string {
  const expiryDate = DateTime.fromISO(endDate);
  const currentDate = DateTime.now();
  const diff = expiryDate.diff(currentDate, ['days']);
  let expirationTime = diff.toObject();
  if (expirationTime.days && expirationTime.days <= 1) {
    expirationTime = expiryDate.diff(currentDate, ['hours', 'minutes']).toObject();
    expirationTime = { ...expirationTime, minutes: expirationTime.minutes ? Math.floor(expirationTime.minutes) : 0 };
  } else {
    expirationTime = { ...expirationTime, days: expirationTime.days ? Math.floor(expirationTime.days) : 0 };
  }
  return Duration.fromObject(expirationTime, { locale: DefaultLanguage }).toHuman({ unitDisplay: 'short' });
}

export function toAppId(appId: string): AppId {
  switch (appId.toLowerCase()) {
    case 'arena':
      return AppId.Arena;
    case 'assetcenter':
      return AppId.AssetCenter;
    case 'datamosaix':
      return AppId.DataMosaix;
    case 'digitalengineering':
      return AppId.DigitalEngineering;
    case 'digitaltechtransfer':
      return AppId.DigitalTechTransfer;
    case 'edm':
      return AppId.EDM;
    case 'elementary':
      return AppId.Elementary;
    case 'emulate3d':
      return AppId.Emulate3D;
    case 'fooservice':
      return AppId.FooService;
    case 'ide':
      return AppId.IDE;
    case 'energymanager':
      return AppId.EnergyManager;
    case 'batchperformanceanalytics':
      return AppId.BatchPerformanceAnalytics;
    case 'ftoptix':
      return AppId.FTOptix;
    case 'ftremoteactivationmanager':
      return AppId.FTRemoteActivationManager;
    case 'secureremoteaccess':
      return AppId.SecureRemoteAccess;
    case 'vault':
      return AppId.Vault;
    case 'ppaxanalytics':
      return AppId.PPaxAnalytics;
    case 'mepm':
      return AppId.MEPM;
    case 'cds':
      return AppId.CDS;
    case 'logix5000':
      return AppId.Logix5000;
    default:
      throw new Error(`Unknown appId/servicekind from backend '${appId}'`);
  }
}

export const notFoundError = "We couldn't find what you are looking for. Please check the URL and try again.";
export const forbiddenError = 'Insufficient Permissions. Please contact organization admin to perform this operation';
export const badRequestError = 'The request was invalid.';
export const conflictError = 'Request sent successfully. You will be notified once your request is approved.';
export const alreadyGoneError = 'The resource has been deleted. Please refresh';
export const tooManyRequestsError = (retryAfterSeconds: number): string => {
  return `Please retry again after ${retryAfterSeconds} seconds.`;
};
export const gatewayTimeout = 'Operation Timeout. Please refresh.';
export const internalServerError = "We're experiencing technical difficulties. Please try again in a few moments";

export const tenantArchivedReadOnlyError = 'An administrator may have archived or modified the organization.';

export const defaultErrorHandlers: Partial<Record<HttpStatusCode, (apiError: ApiError, _body: any) => Action[]>> = {
  [HttpStatusCode.NotFound]: displaySnackBarMessage(notFoundError),
  [HttpStatusCode.Forbidden]: displaySnackBarMessage(forbiddenError),
  [HttpStatusCode.BadRequest]: displaySnackBarMessage(badRequestError),
  [HttpStatusCode.Conflict]: (apiError: ApiError, _body: any): Action[] => {
    // Now, we have ErrorData.outcome check for overllaping entitlement /api/tenants/entitlements/{id}
    // This has to be at default/global level as it deals with TenantArchival
    // In future would have to move away from ErrorCode to ErrorData.Outcome for all apis
    if (
      apiError.ErrorCode === AppErrorCode.TenantArchivedReadOnly ||
      apiError.ErrorData.outcome === AppErrorCode.TenantIsArchived
    ) {
      const actions: Action[] = [];
      actions.push(noLastAccessedTenant());
      return actions;
    }
    return [displayMessageAction(conflictError, 'Success')];
  },
  [HttpStatusCode.Gone]: displaySnackBarMessage(alreadyGoneError),
  [HttpStatusCode.TooManyRequests]: (apiError: ApiError, _body: any): Action[] => {
    const retryAfterSeconds = DateTime.fromISO(apiError.RetryAfter).second;
    return [displayMessageAction(tooManyRequestsError(retryAfterSeconds))];
  },
  [HttpStatusCode.GatewayTimeout]: displaySnackBarMessage(gatewayTimeout),
  [HttpStatusCode.InternalServerError]: (apiError: ApiError, _body: any): Action[] => {
    return [displayMessageAction(internalServerError), navigateToError({ payload: apiError })];
  },
};
export function displayMessageAction(message: string, type?: SnackBarType): Action {
  return UiStateActions.displayMessage({
    payload: {
      message: message,
      type: type ?? 'Error',
    },
  });
}

export function displaySnackBarMessage(message: string, type?: SnackBarType) {
  return (_apiError: ApiError, _body: any): Action[] => [displayMessageAction(message, type)];
}

interface EnvConfig {
  env: Env;
  subDomain: string;
  /** Override URL pattern — defaults to `<subDomain>.rockwellautomation.com` */
  urlPattern?: RegExp;
}

const ENV_CONFIG: EnvConfig[] = [
  { env: 'sandbox', subDomain: 'lemans-sandbox' },
  { env: 'non-prod', subDomain: 'cloud-dev' },
  { env: 'demo', subDomain: 'demo' },
  { env: 'pre-prod', subDomain: 'cloud-preprod' },
  { env: 'production', subDomain: 'cloud' },
  // Using sandbox subdomain for local environment as well since we want to point to sandbox url for default logo and other assets
  { env: 'local', subDomain: 'lemans-sandbox', urlPattern: /home\.lemans/i },
];

export class Environment {
  public static getInstance(csPortalUrl: string): Environment {
    if (!Environment.instance) {
      if (!csPortalUrl) {
        throw new Error(
          'Environment not initialized. Call Environment.getInstance(csPortalUrl) with a valid URL first.',
        );
      }
      Environment.instance = new Environment(csPortalUrl);
    }
    return Environment.instance;
  }

  public get subDomain(): string {
    return this.config.subDomain;
  }

  public get env(): Env {
    return this.config.env;
  }

  private static instance: Environment;
  private readonly config: EnvConfig;

  private static detectConfig(csPortalUrl: string): EnvConfig {
    const match = ENV_CONFIG.find(({ subDomain, urlPattern }) => {
      const pattern = urlPattern ?? new RegExp(String.raw`${subDomain}\.rockwellautomation\.com`, 'i');
      return pattern.test(csPortalUrl);
    });
    if (!match) {
      throw new Error(`Unknown environment for url: ${csPortalUrl}`);
    }
    return match;
  }

  private constructor(csPortalUrl: string) {
    this.config = Environment.detectConfig(csPortalUrl);
  }
}

export function sanitizedCellRenderer(
  domSanitizer: DomSanitizer,
  thisObj: any,
  renderFn: (params: ICellRendererParams) => string,
): (params: ICellRendererParams) => string {
  const safeRenderer = function (params: ICellRendererParams): string {
    return domSanitizer.sanitize(SecurityContext.HTML, renderFn.bind(thisObj)(params)) as string;
  };
  return safeRenderer;
}
export function canManageTenant(resources: Resource[]): boolean {
  return resources.some((resource) => resource.resourceType === ResourceType.Tenant);
}

export function canManageService(resources: Resource[]): boolean {
  return resources.some((resource) => resource.resourceType === ResourceType.Service);
}

export function canManageAny(resources: Resource[]): boolean {
  return canManageTenant(resources) || canManageService(resources);
}

/**
 * Returns an array of tenant action menu items based on the user's role.
 *
 * @param role - The role of the user (Owner, Admin, Contributor, or Navigator)
 * @returns {TenantActionItem[]} An array of menu items containing labels, route paths, and other properties.
 *                              Returns an empty array if the role doesn't match any predefined roles.
 *
 * Each TenantActionItem can contain:
 * - label: Display text for the menu item
 * - routePath: Navigation path for the menu item
 * - isDivider: Boolean indicating if item is a divider
 * - disabled: Boolean indicating if item is disabled
 */
export function getTenantActionMenu(tenantRow: TenantRow, isOnSubOrgPage: boolean): TenantActionItem[] {
  const getTooltipText = (tenantRow: TenantRow): string => {
    if (tenantRow.hasAnyOwnerRole) {
      return `You are the owner of the ${isOnSubOrgPage ? 'sub-organization' : 'organization'} or any of its services and must transfer that ownership in order to leave.`;
    } else if (!tenantRow.hasDirectRole) {
      if (isOnSubOrgPage) {
        return 'You cannot leave this sub-organization because your access is inherited from a parent organization.';
      }
      return `You cannot leave this organization because you don't hold a direct role or access to any of its services.`;
    }
    return '';
  };
  const actionMap = new Map<string, TenantActionItem[]>([
    [
      'OwnerOrAdmin',
      [
        { label: 'Applications', routePath: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/:orgId/dashboard` },
        { label: 'Sub-organizations', routePath: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/:orgId/suborgs` },
        { label: 'Manage user access', routePath: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/:orgId/access-mngmt` },
        { label: 'Send invites', routePath: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/:orgId/invite-users` },
        { label: 'Approve users', routePath: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/:orgId/approve-users` },
        { label: 'Organization entitlements', routePath: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/:orgId/entitlement` },
        // RALMCSUE-4841 - Hide the Universal credits balance. Don’t remove the code, in case we need it in the future.
        // { label: 'Universal credits balance', routePath: '/universalcredits/ledger' },
        { label: 'Settings', routePath: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/:orgId/settings` },
        { isDivider: true },
        {
          label: 'Leave...',
          routePath: '',
          disabled: tenantRow.hasAnyOwnerRole || !tenantRow.hasDirectRole,
          tooltip: getTooltipText(tenantRow),
        },
      ],
    ],
    [
      'ContributorOrNavigator',
      [
        { label: 'Applications', routePath: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/:orgId/dashboard` },
        { label: 'Sub-organizations', routePath: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/:orgId/suborgs` },
        { isDivider: true },
        {
          label: 'Leave...',
          routePath: '',
          disabled: tenantRow.hasAnyOwnerRole || !tenantRow.hasDirectRole,
          tooltip: getTooltipText(tenantRow),
        },
      ],
    ],
  ]);

  switch (tenantRow.role) {
    case Role.Owner:
    case Role.Admin:
      return actionMap.get('OwnerOrAdmin') ?? [];

    case Role.Contributor:
    case Role.Navigator:
      return actionMap.get('ContributorOrNavigator') ?? [];

    default:
      return [];
  }
}

export const leaveOrgDialogConfig: IDialogConfig = {
  title: 'Leave organization',
  message: '',
  size: DialogSizes.S,
  buttons: [
    {
      label: 'Leave',
      buttonStyle: ActionButtonStyles.Main,
      shouldAutofocus: true,
    },
    {
      label: 'Cancel',
    },
  ],
  hideTitleIcon: true,
  showCloseIconButton: true,
};

export function routesAccessibleWithoutTenant(appFlow: AppFlow): boolean {
  return [AppFlow.JoinOrgRequest, AppFlow.RedeemTrial, AppFlow.Entitlement, AppFlow.Invitation].includes(appFlow);
}

export function getAppFlow(url): AppFlow {
  const parsedURL = new URL(url, globalThis.location.origin);
  const regexForRedeemTrialFlow = /^\/campaigns\/[^/]+\/reservations\/[^/]+\/redeem$/;
  if (regexForRedeemTrialFlow.test(parsedURL.pathname)) {
    return AppFlow.RedeemTrial;
  }
  if (parsedURL.searchParams.has('invitationId')) {
    return AppFlow.Invitation;
  }
  const regexForJoinOrgRequestFlow = /^\/request\/[a-zA-Z0-9_.-]+$/;
  if (regexForJoinOrgRequestFlow.test(parsedURL.pathname)) {
    return AppFlow.JoinOrgRequest;
  }
  const regexForEntitlementFlow = /^\/entitlement$/;
  if (regexForEntitlementFlow.test(parsedURL.pathname)) {
    return AppFlow.Entitlement;
  }
  return AppFlow.Dashboard;
}

export function replaceTenantLogoUrlHost(originalTenantLogoUrl: string, newHost: string): string {
  const logoUrl = new URL(originalTenantLogoUrl);
  const updateLogoUrl = new URL(`${newHost}${logoUrl.pathname}`);
  return updateLogoUrl.toString();
}

export function appendTenantLogoEtag(originalTenantLogoUrl: string, etag: string): string {
  const logoUrl = new URL(originalTenantLogoUrl);
  // The logo proxy returns 403 for other query keys, so use numeric _cb format.
  const normalizedEtag = etag.replaceAll(/^W\//g, '').replaceAll(/(^"|"$)/g, '');
  const cacheVersion = /^(?:0x[0-9a-f]+)$/i.test(normalizedEtag) ? BigInt(normalizedEtag).toString(10) : normalizedEtag;
  logoUrl.searchParams.set('_cb', cacheVersion);
  return logoUrl.toString();
}

export function preserveTenantLogoCacheBust(logoUrl: string, cachedLogoUrl?: string): string {
  if (!logoUrl || !cachedLogoUrl) {
    return logoUrl;
  }

  const cacheVersion = new URL(cachedLogoUrl).searchParams.get('_cb');
  if (!cacheVersion) {
    return logoUrl;
  }

  const updatedLogoUrl = new URL(logoUrl);
  updatedLogoUrl.searchParams.set('_cb', cacheVersion);
  return updatedLogoUrl.toString();
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

export function flagValueToBoolean(value: FlagValue): boolean {
  return value === true || (typeof value === 'string' && value.toLowerCase() === 'true');
}
